import { PDFDocument, rgb, StandardFonts, type PDFFont } from 'pdf-lib';
import { coordenadasPDF, MAX_OBSERVACOES_PROPOSTA } from '@/config/coordenadasPDF';

interface Orcamento {
  id: number;
  projeto_id?: number;
  forma_pagamento?: string;
  plano_id?: string;
  observacoes?: string | null;
  cliente?: {
    nome: string;
    cpf_cnpj?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    telefone?: string;
    email?: string;
  };
  projeto?: {
    observacoes?: string | null;
    equipamentos?: Array<{
      equipamento?: {
        marca?: string;
        modelo?: string;
        potencia?: string;
        tipo?: string;
      };
      quantidade: number;
    }>;
    id_projeto_elevo?: string;
    empresa_projeto_externo_id?: string;
  };
  quantidade_placas?: number;
  vigencia_anos?: number;
  plano?: {
    nome?: string;
    vigencia_anos?: number;
    valor_por_painel?: number;
  };
  valor_total?: number;
  valor_seguro?: number;
  valor_tabela?: number | null;
  desconto_valor?: number | null;
  desconto_concedido_por?: string | null;
  created_at?: string;
}

function multiplicadorVigenciaPdf(anos: number): number {
  switch (anos) {
    case 2:
      return 1.8;
    case 3:
      return 2.5;
    case 5:
      return 3.8;
    case 10:
      return 6.5;
    default:
      return anos > 0 ? 1 : 1;
  }
}

/** Remove texto automático gerado ao criar orçamento; mantém observação do usuário. */
function limparObservacoesAutomaticas(texto: string | null | undefined): string {
  if (!texto?.trim()) return '';
  return texto
    .replace(/Orçamento gerado do projeto \d+\.[\s\S]*$/i, '')
    .trim();
}

function quebrarTextoEmLinhas(
  texto: string,
  font: PDFFont,
  fontSize: number,
  maxWidth: number
): string[] {
  const linhas: string[] = [];
  const paragrafos = texto.replace(/\r\n/g, '\n').split('\n');

  for (const paragrafo of paragrafos) {
    const palavras = paragrafo.trim().split(/\s+/).filter(Boolean);
    if (palavras.length === 0) {
      linhas.push('');
      continue;
    }

    let linhaAtual = '';
    for (const palavra of palavras) {
      const tentativa = linhaAtual ? `${linhaAtual} ${palavra}` : palavra;
      if (font.widthOfTextAtSize(tentativa, fontSize) <= maxWidth) {
        linhaAtual = tentativa;
        continue;
      }

      if (linhaAtual) linhas.push(linhaAtual);

      if (font.widthOfTextAtSize(palavra, fontSize) <= maxWidth) {
        linhaAtual = palavra;
      } else {
        // Quebra palavra longa para não sair da caixa
        let resto = palavra;
        while (resto) {
          let cortado = resto.length;
          while (
            cortado > 1 &&
            font.widthOfTextAtSize(resto.slice(0, cortado), fontSize) > maxWidth
          ) {
            cortado -= 1;
          }
          linhas.push(resto.slice(0, cortado));
          resto = resto.slice(cortado);
        }
        linhaAtual = '';
      }
    }
    if (linhaAtual) linhas.push(linhaAtual);
  }

  return linhas;
}

// Mapeamento de forma de pagamento
function mapearFormaPagamento(valor: string | undefined): string {
  if (!valor) return '';
  if (valor === '1') return 'À Vista';
  if (valor === '2') return 'Parcelado';
  return valor;
}

export async function preencherPdfProposta(orcamento: Orcamento): Promise<Blob> {
  try {
    console.log('🔄 Iniciando preenchimento do PDF template...');
    
    // Buscar ID externo e observações do projeto se projeto_id existir
    let idProjetoEmpresa = '';
    let observacoesProjeto = limparObservacoesAutomaticas(
      (orcamento as any).projeto?.observacoes ?? orcamento.observacoes
    );
    if (orcamento.projeto_id) {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: projeto } = await supabase
          .from('projetos')
          .select('empresa_projeto_externo_id, id_projeto_elevo, observacoes')
          .eq('id', orcamento.projeto_id)
          .single();
        idProjetoEmpresa = projeto?.empresa_projeto_externo_id || projeto?.id_projeto_elevo || '';
        if (projeto?.observacoes?.trim()) {
          observacoesProjeto = projeto.observacoes.trim();
        }
      } catch (error) {
        console.warn('⚠️ Não foi possível carregar dados do projeto, seguindo sem eles:', error);
      }
    } else if ((orcamento as any).projeto?.empresa_projeto_externo_id || (orcamento as any).projeto?.id_projeto_elevo) {
      idProjetoEmpresa = (orcamento as any).projeto.empresa_projeto_externo_id || (orcamento as any).projeto.id_projeto_elevo;
    }
    
    // Carregar o PDF template
    const templateResponse = await fetch('/SUNPRO _PROPOSTA_COMERCIAL_NOVO.pdf');
    const templateBytes = await templateResponse.arrayBuffer();
    
    // Carregar o PDF
    const pdfDoc = await PDFDocument.load(templateBytes);
    const pages = pdfDoc.getPages();
    
    // Obter a data atual formatada
    const dataAtual = new Date(orcamento.created_at || new Date()).toLocaleDateString('pt-BR');
    
    // Calcular informações técnicas
    // Equipamentos podem vir em diferentes formatos dependendo de onde o orçamento foi carregado:
    // 1) orcamento.equipamentos (lista com join de equipamento)
    // 2) orcamento.projeto?.equipamentos (quando vem do projeto)
    const equipamentosOrigem: any[] = (orcamento as any).equipamentos && Array.isArray((orcamento as any).equipamentos)
      ? (orcamento as any).equipamentos
      : (orcamento.projeto?.equipamentos || []);

    // Normalizar para garantir estrutura { equipamento: { tipo, marca, modelo, potencia }, quantidade }
    const equipamentos = equipamentosOrigem.map((eq: any) => {
      if (eq?.equipamento) {
        return {
          quantidade: eq.quantidade ?? 0,
          equipamento: {
            tipo: eq.equipamento.tipo ?? '',
            marca: eq.equipamento.marca ?? '',
            modelo: eq.equipamento.modelo ?? '',
            potencia: eq.equipamento.potencia ?? ''
          }
        };
      }
      // Caso extremo: veio achatado sem "equipamento" aninhado
      return {
        quantidade: eq.quantidade ?? 0,
        equipamento: {
          tipo: eq.tipo ?? '',
          marca: eq.marca ?? '',
          modelo: eq.modelo ?? '',
          potencia: eq.potencia ?? ''
        }
      };
    });
    const paineis = equipamentos.filter(eq => eq.equipamento?.tipo?.toLowerCase() === 'painel');
    const inversores = equipamentos.filter(eq => eq.equipamento?.tipo?.toLowerCase() === 'inversor');
    
    const quantidadePaineisEquip = paineis.reduce((total, eq) => total + eq.quantidade, 0);
    const quantidadeInversores = inversores.reduce((total, eq) => total + eq.quantidade, 0);
    const quantidadePaineis =
      quantidadePaineisEquip > 0
        ? quantidadePaineisEquip
        : Number(orcamento.quantidade_placas) || 0;
    
    // Calcular potência total
    // A potência está armazenada em Watts (ex: "550W" ou "550")
    // Precisamos converter para kW dividindo por 1000
    const potenciaTotal = paineis.reduce((total, eq) => {
      // Remove qualquer unidade (W, kW, etc) e converte para número
      const potenciaStr = (eq.equipamento?.potencia || '0').toString().replace(/[^0-9.,]/g, '').replace(',', '.');
      const potencia = parseFloat(potenciaStr) || 0;
      // Soma em Watts
      return total + (potencia * eq.quantidade);
    }, 0) / 1000; // Converte de W para kW
    
    // Obter marca do primeiro painel (assumindo que todos são da mesma marca)
    const marcaPaineis = paineis[0]?.equipamento?.marca || '';
    const marcaInversores = inversores[0]?.equipamento?.marca || '';
    const modeloInversores = inversores[0]?.equipamento?.modelo || '';

    // Buscar valor_por_painel do plano (fallback se valor_seguro não estiver gravado)
    let valorPorPainel = (orcamento as any).plano?.valor_por_painel || 0;
    if (!valorPorPainel && orcamento.plano_id) {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: planoRow } = await supabase
          .from('planos_seguro')
          .select('valor_por_painel')
          .eq('id', orcamento.plano_id)
          .single();
        valorPorPainel = planoRow?.valor_por_painel || 0;
      } catch (e) {
        console.warn('⚠️ Não foi possível carregar valor_por_painel; usando 0:', e);
      }
    }
    const vigenciaAnos = Number(orcamento.vigencia_anos) || Number((orcamento as any).plano?.vigencia_anos) || 1;
    const valorTabelaCalculado =
      quantidadePaineis * (Number(valorPorPainel) || 0) * multiplicadorVigenciaPdf(vigenciaAnos);
    const valorTabela =
      orcamento.valor_tabela != null && Number(orcamento.valor_tabela) > 0
        ? Number(orcamento.valor_tabela)
        : valorTabelaCalculado;
    // Prioridade: valor_seguro gravado (já com desconto comercial) > tabela
    const valorSeguro =
      orcamento.valor_seguro != null && Number(orcamento.valor_seguro) > 0
        ? Number(orcamento.valor_seguro)
        : valorTabela;
    const descontoValor =
      orcamento.desconto_valor != null && Number(orcamento.desconto_valor) > 0
        ? Number(orcamento.desconto_valor)
        : Math.max(0, Math.round((valorTabela - valorSeguro) * 100) / 100);

    // Incluir desconto comercial nas observações do PDF quando houver
    if (descontoValor > 0) {
      const fmt = (n: number) =>
        n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      // Usar apenas ASCII/WinAnsi (Helvetica do pdf-lib nao aceita setas Unicode)
      const linhaDesconto = `Desconto comercial: R$ ${fmt(descontoValor)} (tabela R$ ${fmt(valorTabela)} para proposta R$ ${fmt(valorSeguro)}).`;
      observacoesProjeto = observacoesProjeto
        ? `${observacoesProjeto}\n${linhaDesconto}`
        : linhaDesconto;
    }
    
    // Carregar fontes
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const fontSize = 20;
    
    // Mapear labels para chaves nas coordenadas
    const labelToCoordKey: Record<string, keyof typeof coordenadasPDF> = {
      'Proposta N°': 'proposta_numero',
      'Data': 'data',
      'ID Projeto': 'id_projeto',
      'Nome/Razão Social': 'nome_cliente',
      'CPF/CNPJ': 'cpf_cnpj',
      'Endereço': 'endereco',
      'Cidade-Estado': 'cidade_estado',
      'CEP': 'cep',
      'Telefone': 'telefone',
      'E-mail': 'email',
      'QTD. módulos': 'qtd_modulos',
      'Marca painéis': 'marca_paineis',
      'Potência': 'potencia',
      'Potência Total': 'potencia_total',
      'Tipo de Instalação': 'tipo_instalacao',
      'QTD. inversores': 'qtd_inversores',
      'Marca inversores': 'marca_inversores',
      'Modelo inversores': 'modelo_inversores',
      'Tipo': 'tipo_plano',
      'Vigência': 'vigencia',
      'QTD. Módulos': 'qtd_modulos_valores',
      'Valor Total': 'valor_total',
      'Nome cliente assinatura': 'nome_cliente_assinatura',
      'CPF/CNPJ assinatura': 'cpf_cnpj_assinatura',
      'Local e Data': 'local_data',
      'Forma de Pagamento': 'forma_pagamento'
    };

    // Usar coordenadas configuráveis (apenas label, valor e chave; x/y serão resolvidos na hora de desenhar)
    const campos = [
      // Informações da proposta
      { text: orcamento.id.toString(), label: 'Proposta N°', coordKey: 'proposta_numero' as const },
      { text: dataAtual, label: 'Data', coordKey: 'data' as const },
      { text: idProjetoEmpresa, label: 'ID Projeto', coordKey: 'id_projeto' as const },
      
      // Dados do cliente
      { text: orcamento.cliente?.nome || '', label: 'Nome/Razão Social', coordKey: 'nome_cliente' as const },
      { text: orcamento.cliente?.cpf_cnpj || '', label: 'CPF/CNPJ', coordKey: 'cpf_cnpj' as const },
      { text: [orcamento.cliente?.endereco, orcamento.cliente?.numero].filter(Boolean).join(', ') || '', label: 'Endereço', coordKey: 'endereco' as const },
      { text: [orcamento.cliente?.cidade, orcamento.cliente?.estado].filter(Boolean).join(' - ') || '', label: 'Cidade-Estado', coordKey: 'cidade_estado' as const },
      { text: orcamento.cliente?.cep || '', label: 'CEP', coordKey: 'cep' as const },
      { text: orcamento.cliente?.telefone || '', label: 'Telefone', coordKey: 'telefone' as const },
      { text: orcamento.cliente?.email || '', label: 'E-mail', coordKey: 'email' as const },
      
      // Especificações técnicas
      { text: quantidadePaineis.toString(), label: 'QTD. módulos', coordKey: 'qtd_modulos' as const },
      { text: marcaPaineis, label: 'Marca painéis', coordKey: 'marca_paineis' as const },
      { text: paineis[0]?.equipamento?.potencia || '', label: 'Potência', coordKey: 'potencia' as const },
      { text: `${potenciaTotal.toFixed(3)} KWP`, label: 'Potência Total', coordKey: 'potencia_total' as const },
      { text: 'Residencial', label: 'Tipo de Instalação', coordKey: 'tipo_instalacao' as const },
      { text: quantidadeInversores.toString(), label: 'QTD. inversores', coordKey: 'qtd_inversores' as const },
      { text: marcaInversores, label: 'Marca inversores', coordKey: 'marca_inversores' as const },
      { text: modeloInversores, label: 'Modelo inversores', coordKey: 'modelo_inversores' as const },
      
      // Plano de proteção
      { text: orcamento.plano?.nome || '', label: 'Tipo', coordKey: 'tipo_plano' as const },
      { text: `${orcamento.vigencia_anos || orcamento.plano?.vigencia_anos || 1} ano(s)`, label: 'Vigência', coordKey: 'vigencia' as const },
      
      // Detalhamento de valores (usa valor_seguro gravado — já com desconto comercial)
      { text: quantidadePaineis.toString(), label: 'QTD. Módulos', coordKey: 'qtd_modulos_valores' as const },
      { text: (valorSeguro || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), label: 'Valor Total', coordKey: 'valor_total' as const },
      // Forma de pagamento (página 2)
      { text: mapearFormaPagamento(orcamento.forma_pagamento), label: 'Forma de Pagamento', coordKey: 'forma_pagamento' as const },
      
      // Aceite da proposta
      { text: orcamento.cliente?.nome || '', label: 'Nome cliente assinatura', coordKey: 'nome_cliente_assinatura' as const },
      { text: orcamento.cliente?.cpf_cnpj || '', label: 'CPF/CNPJ assinatura', coordKey: 'cpf_cnpj_assinatura' as const },
      // Local e Data SEMPRE com data atual do sistema:
      { text: `Maringá - PR, ${new Date().toLocaleDateString('pt-BR')}`.trim(), label: 'Local e Data', coordKey: 'local_data' as const }
    ];
    
    console.log('📝 Preenchendo campos no PDF...');
    
    // Preencher cada campo na página correta (padrão página 1)
    campos.forEach(campo => {
      if (!campo.text && campo.text !== '0') return; // Permitir zero como valor válido
      const coords = (coordenadasPDF as any)[campo.coordKey];
      if (!coords) {
        console.warn(`⚠️ Coordenadas ausentes para '${campo.coordKey}'`);
        return;
      }
      const pageIndex = (coords.page ?? 1) - 1;
      const page = pages[pageIndex] || pages[0];
      const isValorTotal = campo.coordKey === 'valor_total';
      const drawSize = isValorTotal ? fontSize + 8 : fontSize;
      const drawFont = isValorTotal ? fontBold : font;
      const textoSeguro = String(campo.text)
        .replace(/[→←↔⇒⇔]/g, '->')
        .replace(/[–—]/g, '-')
        .replace(/[“”„]/g, '"')
        .replace(/[‘’]/g, "'")
        .replace(/…/g, '...')
        .replace(/[^\x00-\xFF]/g, '');
      if (!textoSeguro) return;
      page.drawText(textoSeguro, {
        x: coords.x,
        y: coords.y,
        size: drawSize,
        font: drawFont,
        color: rgb(0, 0, 0),
      });
      console.log(`✅ Campo '${campo.label}' (página ${pageIndex + 1}) preenchido com: ${textoSeguro}`);
    });

    // Observações do projeto na caixa OBSERVAÇÕES (página 2)
    if (observacoesProjeto) {
      const coordsObs = coordenadasPDF.observacoes;
      const pageObs = pages[(coordsObs.page ?? 2) - 1] || pages[pages.length - 1];
      const fontSizeObs = coordsObs.fontSize ?? 13;
      const lineHeight = coordsObs.lineHeight ?? 18;
      const maxWidth = coordsObs.maxWidth ?? 1140;
      const maxLines = coordsObs.maxLines ?? 7;

      const textoLimitado =
        observacoesProjeto.length > MAX_OBSERVACOES_PROPOSTA
          ? `${observacoesProjeto.slice(0, MAX_OBSERVACOES_PROPOSTA - 1).trimEnd()}…`
          : observacoesProjeto;

      let linhas = quebrarTextoEmLinhas(textoLimitado, font, fontSizeObs, maxWidth);
      if (linhas.length > maxLines) {
        linhas = linhas.slice(0, maxLines);
        const ultima = linhas[maxLines - 1] || '';
        linhas[maxLines - 1] =
          ultima.length > 3 ? `${ultima.slice(0, -1).trimEnd()}…` : `${ultima}…`;
      }

      linhas.forEach((linha, index) => {
        if (!linha) return;
        // Helvetica (WinAnsi): remover caracteres fora do encoding
        const linhaSegura = linha
          .replace(/[→←↔⇒⇔]/g, '->')
          .replace(/[–—]/g, '-')
          .replace(/[“”„]/g, '"')
          .replace(/[‘’]/g, "'")
          .replace(/…/g, '...')
          .replace(/[^\x00-\xFF]/g, '');
        if (!linhaSegura) return;
        pageObs.drawText(linhaSegura, {
          x: coordsObs.x,
          y: coordsObs.y - index * lineHeight,
          size: fontSizeObs,
          font,
          color: rgb(0, 0, 0),
        });
      });
      console.log(`✅ Observações preenchidas (${linhas.length} linha(s))`);
    }
    
    // Salvar o PDF preenchido
    const pdfBytes = await pdfDoc.save();
    
    console.log('✅ PDF preenchido com sucesso!');
    
    // Garantir compatibilidade de tipo para BlobPart
    const pdfArrayBuffer = pdfBytes instanceof Uint8Array
      ? (pdfBytes.buffer as ArrayBuffer).slice(0)
      : (pdfBytes as unknown as ArrayBuffer);
    return new Blob([pdfArrayBuffer], { type: 'application/pdf' });
    
  } catch (error) {
    console.error('❌ Erro ao preencher PDF:', error);
    throw new Error(`Erro ao preencher PDF: ${error}`);
  }
}

// Função para fazer download do PDF preenchido
export async function baixarPdfPropostaPreenchida(orcamento: Orcamento) {
  try {
    const pdfBlob = await preencherPdfProposta(orcamento);
    
    // Criar URL para download
    const url = URL.createObjectURL(pdfBlob);

    // Tentar abrir em nova aba (mais robusto contra bloqueio de pop-up/download)
    const filename = `Proposta_Comercial_${orcamento.id}_${orcamento.cliente?.nome?.replace(/\s+/g, '_') || 'Cliente'}.pdf`;
    const newTab = window.open(url, '_blank');

    if (!newTab || newTab.closed) {
      // Fallback: tentar via clique programático
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.rel = 'noopener';
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      document.body.removeChild(link);
    }

    // Revogar URL após um tempo para não quebrar a aba recém-aberta
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    
    console.log('📥 Download do PDF iniciado!');
    
  } catch (error) {
    console.error('❌ Erro ao baixar PDF:', error);
    throw error;
  }
}
